import React, { PropsWithChildren, useState } from 'react';
import { FastField as Field, Form, Formik } from 'formik';
import { Link as RouterLink } from 'react-router-dom';
import { TextField } from 'formik-mui';
import { Grid, Link, MenuItem, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import TermsDialog from '../Dialogs/TermsDialog';
import industries from '../../utils/companyIndustries';
import registrationSchema from 'FeatureModules/Auth/schemas/auth.schemas';
import PasswordInputField from './PasswordInputField';
import AcceptTermsCheckbox from '../AcceptTermsCheckbox';
import { ThemeButton, FieldWrapper } from 'DesignSystem';
import { IRegisterValues } from 'FeatureModules/Auth/types/auth.types';

const TrialText = styled('p')(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

interface IProps extends PropsWithChildren {
  email?: string;
  isPartnerRegistration?: boolean;
  onSubmit: (values: IRegisterValues) => void;
}

const RegistrationForm: React.FC<IProps> = ({
  onSubmit,
  isPartnerRegistration = false,
  email = '',
}) => {
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <Formik<IRegisterValues>
      initialValues={
        email ? { ...registrationSchema.default(), email } : { ...registrationSchema.default() }
      }
      validateOnBlur={false}
      validateOnChange={true}
      validationSchema={registrationSchema}
      onSubmit={values => onSubmit(values)}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container>
            <Grid item sm={12}>
              <Grid container spacing={3}>
                <Grid item sm={12} md={10}>
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <FieldWrapper label="First Name">
                        <Field
                          component={TextField}
                          placeholder="Jane"
                          name="firstName"
                          variant="outlined"
                          inputProps={{
                            'data-testId': 'first-name-field',
                          }}
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={6}>
                      <FieldWrapper label="Last name">
                        <Field
                          component={TextField}
                          placeholder="Doe"
                          name="lastName"
                          variant="outlined"
                          inputProps={{
                            'data-testId': 'last-name-field',
                          }}
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldWrapper label="Company name">
                        <Field
                          component={TextField}
                          fullWidth
                          placeholder="Transparency Inc."
                          name="companyName"
                          variant="outlined"
                          inputProps={{
                            'data-testId': 'company-name-field',
                          }}
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldWrapper label="Industry">
                        <Field
                          component={TextField}
                          type="text"
                          name="industry"
                          variant="outlined"
                          select
                          fullWidth
                          data-testid="select-industry"
                        >
                          {industries.map(({ displayName, value }) => (
                            <MenuItem key={value} value={value} data-testid="dropdown-item">
                              {displayName}
                            </MenuItem>
                          ))}
                        </Field>
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldWrapper label="Email">
                        <Field
                          disabled={isPartnerRegistration && email}
                          component={TextField}
                          fullWidth
                          placeholder="jane.doe@transparency.org"
                          name="email"
                          variant="outlined"
                          inputProps={{
                            'data-testId': 'email-field',
                          }}
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldWrapper label="Password">
                        <Field
                          fullWidth
                          component={PasswordInputField}
                          name="password"
                          placeholder="Min. 8 characters"
                          variant="outlined"
                          inputProps={{
                            'data-testId': 'password-field',
                          }}
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldWrapper label="Confirm password">
                        <Field
                          fullWidth
                          component={PasswordInputField}
                          name="confirmPassword"
                          placeholder="Confirm password"
                          variant="outlined"
                          inputProps={{
                            'data-testId': 'confirm-password-field',
                          }}
                        />
                      </FieldWrapper>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} mt={1.5}>
                  <AcceptTermsCheckbox handleOpenTerms={() => setTermsOpen(true)} />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <ThemeButton
                      loading={isSubmitting}
                      size="large"
                      color="PRIMARY"
                      type="submit"
                      className="button-wide"
                      data-testid="register-button"
                    >
                      Register
                    </ThemeButton>
                    {isPartnerRegistration === false && (
                      <TrialText>Starts your 14 day free trial</TrialText>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Link component={RouterLink} to="/login" color="secondary">
                    Already registered? Login
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
        </Form>
      )}
    </Formik>
  );
};

export default RegistrationForm;
